import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import './ui/tokens.css';
import { loadDeviceCredential } from './launcher-device';
import { startPageViews } from './page-views';
import { router } from './router';

/**
 * The client, configured once.
 *
 * `retry: false` on 4xx: a 401 or a 403 is an answer, not a hiccup, and
 * retrying it three times only delays telling the person what happened.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status ?? 0;
        return status >= 500 && failureCount < 2;
      },
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('No #root element.');

/*
 * The device credential, read before anything renders.
 *
 * `api.ts` picks a credential synchronously on every request and prefers this
 * one, so loading it lazily would mean the first calls of every launcher run
 * went out on the session instead — which works, and quietly defeats the point:
 * those are exactly the calls made when a session is most likely to have
 * expired since the launcher was last open.
 *
 * Resolved before `render` rather than in an effect, and it cannot fail: in a
 * browser there is no bridge and it resolves to null immediately, and in the
 * launcher every failure inside it is already swallowed. So this delays the
 * first paint by one IPC round trip at most, and never by a network one.
 *
 * **`.then` rather than a top-level `await`.** Vite's default build target is
 * the `modules` set, whose floor is ES2020 — top-level await is ES2022, and
 * esbuild refuses it outright rather than transpiling. One promise costs
 * nothing and builds everywhere.
 */
void loadDeviceCredential().then(() => start(root));

function start(container: HTMLElement): void {
  /*
   * Page views, subscribed before the first render.
   *
   * Before `createRoot` rather than after, because `startPageViews` reports the
   * landing page itself — the one a search result sent somebody to, and the one
   * a subscription alone would never see, since the router publishes
   * transitions and arriving is not one. Ordering it after the render would
   * work equally well today and would quietly stop working the moment the first
   * paint triggered a navigation.
   *
   * It returns an unsubscribe, which is dropped: this lives as long as the
   * document does. See `page-views.ts` — it is a no-op in the launcher.
   */
  startPageViews();

  createRoot(container).render(
    <StrictMode>
      {/*
        No identity provider to wrap this in. Better Auth's client is a module
        the components import, not a React context — so there is no key to
        thread, nothing to configure at the root, and no window between mount
        and "the SDK has loaded" for a query to fire into.

        Sign-in used to live on the storefront, which owned Clerk's routes. It
        lives here now: the storefront deliberately ships no identity SDK, and
        these pages are the only ones on this origin that a signed-out visitor
        ever sees.
      */}
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}
